import { ActionIcon, Divider, Group, Radio, Stack, Text, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import classes from '@/components/Details/DetailsPage.module.css'
import InfiniteScrolling from '@/components/InfiniteScrolling'

import SendDropdownButton from '../Buttons/SendDropdownButton'
import HelpDrawer from '../HelpDrawer/HelpDrawer'
import TooltipTitle from '../TooltipTitle'
import { Tabs } from './ExplorePage'

const FILTER_ITEM_TAGS = ['#rewards', '#art', '#gaming', '#ai', '#nft', '#airdrop']

export interface ExploreTabProps extends Tabs {
  triggerRefetch: () => void
  isAdmin: boolean
}

const ExploreTab: React.FC<ExploreTabProps> = ({
  id = '',
  title = '',
  contentType,
  query,
  suggestionQuery,
  cols,
  queryId = '',
  total,
  tooltip = '',
  filterOptions,
  sortOptions,
  sortDirection = 'desc', // Default
  searchEnabled,
  triggerRefetch,
  isAdmin,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isDownMd = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const [searchParams] = useSearchParams()

  // State
  const [displayed, setDisplayed] = useState(0)
  const [newTotal, setNewTotal] = useState(total)

  const [filter, setFilter] = useState<string>(filterOptions.length > 0 ? filterOptions[0] : '')
  const [sortedBy, setSortedBy] = useState<string>(sortOptions[0])
  const [fieldDirection, setFieldDirection] = useState<'asc' | 'desc'>(sortDirection)
  const [searchTag, setSearchTag] = useState<string>('')

  // Cleanup search tag when same search tag is clicked after clearing search
  useEffect(() => {
    if (!searchParams.get('search')) {
      setSearchTag('')
    }
  }, [searchParams])

  return (
    <>
      <Stack style={{ gap: '10px' }}>
        <TooltipTitle order={isMobile ? 3 : 2} title={title} tooltip={tooltip} />
        <Stack
          style={{
            flexDirection: isDownMd ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isDownMd ? 'flex-start' : 'center',
          }}
        >
          <Text
            style={{
              color: theme.colors.paragraph[0],
              fontSize: theme.fontSizes.lg,
            }}
          >
            {
              t('components.items.collectionTotal', 'Displaying {{count}} out of {{total}}', {
                count: displayed,
                total: newTotal,
              }) as string
            }
          </Text>
        </Stack>
      </Stack>
      <Stack
        style={{
          flexDirection: isDownMd ? 'column' : 'row',
          justifyContent: filterOptions.length > 0 ? 'space-between' : 'flex-end',
          alignItems: isDownMd ? 'flex-end' : 'center',
          width: isDownMd ? '100%' : '',
          gap: isDownMd ? '3px' : '10px',
        }}
      >
        {filterOptions.length > 0 && (
          <Group mt="xs" style={{ alignItems: 'center' }}>
            {filterOptions.map(fl => (
              <Radio key={fl} onChange={() => setFilter(fl)} label={fl} checked={filter === fl} />
            ))}
            {contentType === 'items' && (
              <>
                <Divider orientation="vertical" />
                <Stack style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                  {FILTER_ITEM_TAGS.map(tag => (
                    <Text
                      key={tag}
                      component="span"
                      onClick={() => setSearchTag(tag)}
                      className={classes.collectionLink}
                      style={{
                        textDecoration: searchTag === tag ? 'underline' : 'none',
                      }}
                    >
                      {tag}
                    </Text>
                  ))}
                </Stack>
              </>
            )}
          </Group>
        )}
        <Stack
          style={{
            gap: '5px',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'right',
          }}
        >
          <SendDropdownButton
            selectedOption={sortedBy as any}
            setSelectedOption={setSortedBy}
            options={sortOptions as any[]}
            width={`${Math.max(...sortOptions.map(option => option.length)) * 9}px`}
            label="Sorted by"
            disabled={sortOptions.length === 1}
          />
          <ActionIcon
            style={{
              border: `2px solid ${theme.colors.background4[0]}`,
              borderRadius: '12px',
              backgroundColor: theme.colors.background3[0],
            }}
            onClick={() => {
              setFieldDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
            }}
          >
            {fieldDirection === 'desc' ? <IconArrowDown size={12} /> : <IconArrowUp size={12} />}
          </ActionIcon>
        </Stack>
      </Stack>
      <InfiniteScrolling
        isAdmin={isAdmin}
        triggerRefetch={triggerRefetch}
        // Mandatory
        contentType={contentType}
        query={query}
        suggestionQuery={suggestionQuery}
        total={newTotal}
        // Specific
        setTotal={setNewTotal}
        setDisplayed={setDisplayed}
        // Optional
        searchEnabled={searchEnabled}
        queryId={queryId.toLowerCase()}
        cols={cols}
        filteredBy={filter}
        orderBy={sortedBy}
        orderDirection={fieldDirection}
        searchTag={searchTag}
      />
      {id && <HelpDrawer id={id} />}
    </>
  )
}

export default ExploreTab
